import { adminService } from "@/api/admin/admin.service";
import type { TLoginAttempt, TLoginStats } from "@/api/admin/admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TFilterDef,
} from "@/components/data-grid";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { extractErrorMessage } from "@/utils";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const PAGE_SIZE = 20;

function StatCard({
  title,
  value,
  sub,
  color = "text-foreground",
}: {
  title: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
        {sub ? <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function LoginAttemptsPage() {
  const navigate = useNavigate();
  const [reloadTick, setReloadTick] = useState(0);
  const [stats, setStats] = useState<TLoginStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadStats = useCallback(async (signal: AbortSignal) => {
    setStatsLoading(true);
    setStatsError(null);
    const { data, error: err } = await adminService.getLoginStats(undefined, { signal });
    if (signal.aborted) return;
    if (err) {
      setStatsError(await extractErrorMessage(err));
    } else if (data) {
      setStats(data.data ?? null);
    }
    setStatsLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadStats(ctrl.signal);
    return () => ctrl.abort();
  }, [loadStats, reloadTick]);

  const fetcher = async (query: TDataGridQuery) => {
    // dateRange filters arrive pre-flattened by filtersToParams as `<param>_from` / `<param>_to`.
    const dateFrom = asString(query.filters.created_from);
    const dateTo = asString(query.filters.created_to);
    const failedOnly = query.filters.failed_only === "true";
    const sortParams = query.sort_by
      ? { sort_by: query.sort_by, sort_order: query.sort_order }
      : {};
    const searchParams = query.search ? { search: query.search } : {};

    const result = failedOnly
      ? await adminService.getFailedLoginAttempts(
          {
            limit: query.limit,
            offset: query.offset,
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
            ...searchParams,
            ...sortParams,
          },
          { signal: query.signal }
        )
      : await adminService.getLoginAttempts(
          {
            limit: query.limit,
            offset: query.offset,
            ...(asString(query.filters.user_id) ? { user_id: asString(query.filters.user_id) } : {}),
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
            ...searchParams,
            ...sortParams,
          },
          { signal: query.signal }
        );

    if (result.error) throw result.error;
    return result.data;
  };

  const columns: TColumnDef<TLoginAttempt>[] = [
    { id: "email", header: "Email", type: "email", sortable: true, sortKey: "email", accessor: (r) => r.email ?? "" },
    {
      id: "ip_address",
      header: "IP Address",
      accessor: (r) => r.ip_address ?? "",
      cell: (r) => (
        <span className="font-mono text-xs text-muted-foreground">{r.ip_address ?? "-"}</span>
      ),
    },
    {
      id: "successful",
      header: "Result",
      type: "status",
      sortable: true,
      sortKey: "success",
      accessor: (r) => (r.successful ? "Success" : "Failed"),
    },
    {
      id: "failure_reason",
      header: "Reason",
      type: "text",
      accessor: (r) => r.failure_reason ?? "",
    },
    { id: "created_at", header: "When", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    {
      id: "failed_only",
      type: "select",
      label: "Result",
      options: [
        { label: "All attempts", value: "" },
        { label: "Failed only", value: "true" },
      ],
    },
    { id: "created", type: "dateRange", label: "Date" },
  ];

  const successRate = stats ? `${stats.success_rate.toFixed(1)}%` : "—";

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Login Attempts</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Monitor authentication attempts and investigate suspicious activity
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setReloadTick((t) => t + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : statsError ? (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {statsError}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              title="Total Attempts"
              value={String(stats.total_attempts)}
              sub={`${stats.unique_users} unique users`}
            />
            <StatCard
              title="Successful"
              value={String(stats.successful_attempts)}
              color="text-green-600"
            />
            <StatCard
              title="Failed"
              value={String(stats.failed_attempts)}
              color="text-destructive"
            />
            <StatCard
              title="Success Rate"
              value={successRate}
              sub={`${stats.unique_ips} unique IPs`}
              color={stats.success_rate >= 90 ? "text-green-600" : "text-amber-600"}
            />
          </div>
        ) : null}

        <DataGrid<TLoginAttempt>
          getRowId={(row) => row.id}
          persistKey="admin.login-attempts"
          ariaLabel="Login attempts"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search email or IP",
          }}
          onRowClick={(row) => {
            if (row.user_id) {
              void navigate({
                to: "/app/admin/users/$user_id",
                params: { user_id: row.user_id },
              });
            }
          }}
          rowActions={(row) =>
            row.user_id
              ? [
                  {
                    label: "View user",
                    onClick: () =>
                      void navigate({
                        to: "/app/admin/users/$user_id",
                        params: { user_id: row.user_id! },
                      }),
                  },
                ]
              : []
          }
          emptyState={{ title: "No login attempts found." }}
        />
      </div>
    </div>
  );
}
